// frontend/src/pages/OrdineEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "";

const APP_STATUS_FLOW = [
  { value: "INVIATO", label: "Inviato" },
  { value: "IN_PREPARAZIONE", label: "In preparazione" },
  { value: "EVASO", label: "Evaso" },
  { value: "PRONTO_RITIRO", label: "Pronto al ritiro" },
  { value: "RITIRATO", label: "Ritirato" },
];

const roleToListinoKey = (role) => {
  const R = (role || "").toUpperCase();
  if (R === "AGENTE" || R === "AZIENDA") return "azienda";
  if (R === "FRANCHISING") return "franchising";
  if (R === "PV") return "pv";
  return "cliente";
};

const parseDate = (s) => {
  if (!s) return null;
  const iso = s.includes("T") ? s : s.replace(" ", "T");
  const d = new Date(iso);
  return isNaN(d) ? null : d;
};

// prezzo robusto per ruolo/listino
const priceForRole = (p, meRole) => {
  if (!p) return 0;
  if (p.prezzo !== undefined && p.prezzo !== null) return Number(p.prezzo);
  if (p.price !== undefined && p.price !== null) return Number(p.price);

  const tipo = roleToListinoKey(meRole);
  const key = {
    cliente: "price_client",
    franchising: "price_franchising",
    azienda: "price_azienda",
    pv: "price_pv",
  }[tipo];

  if (key && p[key] !== undefined && p[key] !== null) return Number(p[key]);

  const li = (p.listini || []).find((l) => l.tipo === tipo);
  if (li && li.prezzo !== undefined && li.prezzo !== null) return Number(li.prezzo);
  return 0;
};

// parser robusto array/paginato
const parseList = (text) => {
  let data;
  try { data = text ? JSON.parse(text) : []; } catch { data = []; }
  const items = Array.isArray(data) ? data : data.results || data.items || [];
  return items;
};

export default function OrdineEditor() {
  const { id } = useParams();
  const nav = useNavigate();

  // me
  const [meRole, setMeRole] = useState("");
  const [meId, setMeId] = useState(null);
  const [senderId, setSenderId] = useState(null);
  const isMadre = (meRole || "").toUpperCase() === "MADRE";
  const isAgente = (meRole || "").toUpperCase() === "AGENTE";

  // stato ordine
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [aziendeAgente, setAziendeAgente] = useState([]);
  const [selectedAziendaId, setSelectedAziendaId] = useState("");

  const [status, setStatus] = useState("DRAFT");
  const [appStatus, setAppStatus] = useState("INVIATO");
  const [appStatusLabel, setAppStatusLabel] = useState("Inviato");
  const [senderRole, setSenderRole] = useState("");
  const [updatingAppStatus, setUpdatingAppStatus] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("RITIRO");
  const [messaggio, setMessaggio] = useState("");

  // totali + pagamento
  const [subtotal, setSubtotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [commissionPercent, setCommissionPercent] = useState(0);
  const [commissionAmount, setCommissionAmount] = useState(0);
  // compenso agente (solo ruoli interni)
  const [agentCompStatus, setAgentCompStatus] = useState("");
  const [agentCompPct, setAgentCompPct] = useState(0);
  const [agentCompAmount, setAgentCompAmount] = useState(0);
  const [agentCompPaidAmount, setAgentCompPaidAmount] = useState(null);
  const [agentCompRemainingAmount, setAgentCompRemainingAmount] = useState(null);
  const [agentCompEarnedAt, setAgentCompEarnedAt] = useState(null);
  const [agentCompPaidAt, setAgentCompPaidAt] = useState(null);
  const [payingAgentComp, setPayingAgentComp] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [paidAt, setPaidAt] = useState(null);
  const [orderDate, setOrderDate] = useState(null);

  // extra
  const [perNome, setPerNome] = useState("");
  const [fatt, setFatt] = useState({
    fatt_ragione_sociale: "",
    fatt_piva: "",
    fatt_cf: "",
    fatt_indirizzo: "",
    fatt_cap: "",
    fatt_citta: "",
    fatt_provincia: "",
    fatt_nazione: "",
    fatt_email: "",
    fatt_pec: "",
    fatt_sdi: "",
  });
  const [sped, setSped] = useState({
    sped_destinatario: "",
    sped_indirizzo: "",
    sped_cap: "",
    sped_citta: "",
    sped_provincia: "",
    sped_nazione: "",
    sped_telefono: "",
    sped_note: "",
  });

  // 🔁 sincronia Spedizione = Fatturazione
  const [syncShipBilling, setSyncShipBilling] = useState(false);

  // fattura associata (se esiste)
  const [invoiceId, setInvoiceId] = useState(null);
  const [invoiceStatus, setInvoiceStatus] = useState("");

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("access")}`,
  });
  const jsonHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("access")}`,
  });

  // helper: snapshot della spedizione derivata dalla fatturazione
  const billingToShippingSnapshot = (fatturazione, labelPerNome) => {
    const f = fatturazione || {};
    const dest = (f.fatt_ragione_sociale || "").trim() || (labelPerNome || "").trim();
    return {
      sped_destinatario: dest,
      sped_indirizzo: f.fatt_indirizzo || "",
      sped_cap: f.fatt_cap || "",
      sped_citta: f.fatt_citta || "",
      sped_provincia: f.fatt_provincia || "",
      sped_nazione: f.fatt_nazione || "",
      // telefono e note non hanno un equivalente nella fatturazione
    };
  };

  // carica
  useEffect(() => {
    (async () => {
      try {
        // me
        const meRes = await fetch(`${API_URL}/api/me/`, { headers: authHeaders() });
        const meData = await meRes.json().catch(() => ({}));
        if (meRes.ok) {
          setMeRole((meData?.role || "").toUpperCase());
          setMeId(meData?.id ?? null);
        }

        // Auto-compila dati store per FRANCHISING / PV
        if (meRes.ok && meData?.store) {
          const s = meData.store;
          setPerNome(s.display_name || s.name || "");
          setFatt((prev) => ({
            ...prev,
            fatt_ragione_sociale: s.fatt_ragione_sociale || "",
            fatt_piva: s.fatt_piva || "",
            fatt_cf: s.fatt_cf || "",
            fatt_indirizzo: s.fatt_indirizzo || "",
            fatt_cap: s.fatt_cap || "",
            fatt_citta: s.fatt_citta || "",
            fatt_provincia: s.fatt_provincia || "",
            fatt_nazione: s.fatt_nazione || "",
            fatt_email: s.fatt_email || "",
            fatt_pec: s.fatt_pec || "",
            fatt_sdi: s.fatt_sdi || "",
          }));
          setSped((prev) => ({
            ...prev,
            sped_destinatario: s.fatt_ragione_sociale || s.name || "",
            sped_indirizzo: s.fatt_indirizzo || "",
            sped_cap: s.fatt_cap || "",
            sped_citta: s.fatt_citta || "",
            sped_provincia: s.fatt_provincia || "",
            sped_nazione: s.fatt_nazione || "",
            sped_telefono: s.telefono || "",
          }));
        }

        // aziende agente
        if (((meData?.role || "").toUpperCase()) === "AGENTE") {
          try {
            const azRes = await fetch(`${API_URL}/api/aziende/`, { headers: authHeaders() });
            const azText = await azRes.text();
            const azData = parseList(azText);
            setAziendeAgente(Array.isArray(azData) ? azData : []);
          } catch {
            setAziendeAgente([]);
          }
        }

        // ordine
        const oRes = await fetch(`${API_URL}/api/ordini/${id}/`, { headers: authHeaders() });
        const oData = await oRes.json();
        if (!oRes.ok) throw new Error(oData?.detail || "Errore caricamento ordine");

        setSenderId(oData.sender_id ?? null);
        setSenderRole((oData.sender_role || "").toUpperCase());
        setAppStatus(oData.app_status || "INVIATO");
        setAppStatusLabel(oData.app_status_label || "");
        setStatus(oData.status || "DRAFT");
        setPaymentMethod(oData.payment_method || "RITIRO");
        setMessaggio(oData.messaggio_agente || "");
        setItems(
          (oData.items || []).map((r) => ({
            product: r.product_id ?? r.product?.id ?? r.product ?? "",
            quantity: Number(r.quantity) || 0,
            unit_price: Number(r.unit_price ?? 0),
            line_subtotal: Number(r.line_subtotal ?? 0),
            _name: r.product_name || r.name || "",
          }))
        );
          setPerNome(oData.per_nome || "");
          setFatt(Object.fromEntries(Object.entries(oData.fatturazione || {}).map(([k, v]) => [k, v || ""])));
          setSped(Object.fromEntries(Object.entries(oData.spedizione || {}).map(([k, v]) => [k, v || ""])));

        setSubtotal(Number(oData.subtotal || 0));
        setTotal(Number(oData.total || 0));
        setCommissionPercent(Number(oData.commission_percent || 0));
        setCommissionAmount(Number(oData.commission_amount || 0));
        setIsPaid(Boolean(oData.is_paid));
        // compenso agente
        setAgentCompStatus((oData.agent_comp_status || "").toUpperCase());
        setAgentCompPct(Number(oData.agent_comp_pct || 0));
        setAgentCompAmount(Number(oData.agent_comp_amount || 0));
      setAgentCompPaidAmount(oData.agent_comp_paid_amount == null ? null : Number(oData.agent_comp_paid_amount));
      setAgentCompRemainingAmount(oData.agent_comp_remaining_amount == null ? null : Number(oData.agent_comp_remaining_amount));
        setAgentCompEarnedAt(oData.agent_comp_earned_at || null);
        setAgentCompPaidAt(oData.agent_comp_paid_at || null);
        setPaidAt(oData.paid_at || null);
        setOrderDate(parseDate(oData.date) || null);

        // prodotti se bozza e non MADRE
        const shouldLoadProducts =
          ((oData.status || "DRAFT") === "DRAFT") &&
          ((meData?.role || "").toUpperCase() !== "MADRE");
        if (shouldLoadProducts) {
          const key = roleToListinoKey(meData?.role);
          const pRes = await fetch(
            `${API_URL}/api/prodotti/?tipo_listino=${encodeURIComponent(key)}`,
            { headers: authHeaders() }
          );
          const pText = await pRes.text();
          const pData = parseList(pText);
          setProducts(Array.isArray(pData) ? pData : []);
        }

        // prova a capire se è già stata creata una fattura per questo ordine
        // 1) se l'API ordine restituisce lista fatture
        if (Array.isArray(oData.invoices) && oData.invoices.length) {
          const inv = oData.invoices[0];
          setInvoiceId(inv.id ?? inv.pk ?? null);
          setInvoiceStatus(inv.status || "");
        } else {
          // 2) fallback: endpoint di lista filtrato per order
          try {
            const invRes = await fetch(`${API_URL}/api/fatture/?order=${id}`, { headers: authHeaders() });
            const invText = await invRes.text();
            const invList = parseList(invText);
            if (invList.length) {
              setInvoiceId(invList[0].id);
              setInvoiceStatus(invList[0].status || "");
            }
          } catch {}
        }

        setLoading(false);
      } catch (e) {
        alert("❌ " + (e?.message || "Errore"));
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const editingEnabled = status === "DRAFT" && !isMadre;

  // quando la sincronia è attiva, ogni cambio fatt/perNome aggiorna la spedizione
  useEffect(() => {
    if (syncShipBilling && editingEnabled) {
      setSped((prev) => ({
        ...prev,
        ...billingToShippingSnapshot(fatt, perNome),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncShipBilling, fatt, perNome, editingEnabled]);

  const getUnitPriceLocal = (row) => {
    const found = products.find((p) => Number(p.id) === Number(row.product));
    if (row.unit_price !== undefined && row.unit_price !== null && row.unit_price !== "") {
      return Number(row.unit_price);
    }
    return priceForRole(found, meRole);
  };

  const preview = useMemo(() => {
    const sub = items.reduce((acc, it) => {
      const q = Number(it.quantity) || 0;
      const up = editingEnabled ? getUnitPriceLocal(it) : Number(it.unit_price || 0);
      return acc + q * up;
    }, 0);
    return { subtotal: sub, total: sub };
  }, [items, editingEnabled, products]);

  const setItemField = (idx, key, value) => {
    setItems((arr) => arr.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const addRow = () => {
    const firstId = products.length > 0 ? products[0].id : "";
    setItems((arr) => [...arr, { product: firstId, quantity: 1 }]);
  };
  const removeRow = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const refreshFromPayload = (data) => {
    if (!data) return;
    if (data.items) {
      setItems(
        (data.items || []).map((r) => ({
          product: r.product_id ?? r.product?.id ?? r.product ?? "",
          quantity: Number(r.quantity) || 0,
          unit_price: Number(r.unit_price ?? 0),
          line_subtotal: Number(r.line_subtotal ?? 0),
          _name: r.product_name || r.name || "",
        }))
      );
    }
    if (data.status) setStatus(data.status);
    if (data.app_status !== undefined) setAppStatus(data.app_status || "INVIATO");
    if (data.app_status_label !== undefined) setAppStatusLabel(data.app_status_label || "");
    if (data.sender_role !== undefined) setSenderRole((data.sender_role || "").toUpperCase());
    if (data.payment_method !== undefined) setPaymentMethod(data.payment_method || "");
    if (data.messaggio_agente !== undefined) setMessaggio(data.messaggio_agente || "");
    setSubtotal(Number((data.subtotal ?? subtotal) || 0));
    setTotal(Number((data.total ?? total) || 0));
    setCommissionPercent(Number((data.commission_percent ?? commissionPercent) || 0));
    setCommissionAmount(Number((data.commission_amount ?? commissionAmount) || 0));
    if (data.is_paid !== undefined) setIsPaid(Boolean(data.is_paid));
    // Aggiorna il compenso agente anche quando la risposta non contiene is_paid.
    if (data.agent_comp_status !== undefined) setAgentCompStatus((data.agent_comp_status || "").toUpperCase());
    if (data.agent_comp_pct !== undefined) setAgentCompPct(Number(data.agent_comp_pct || 0));
    if (data.agent_comp_amount !== undefined) setAgentCompAmount(Number(data.agent_comp_amount || 0));
    if (data.agent_comp_paid_amount !== undefined)
      setAgentCompPaidAmount(data.agent_comp_paid_amount == null ? null : Number(data.agent_comp_paid_amount));
    if (data.agent_comp_remaining_amount !== undefined)
      setAgentCompRemainingAmount(data.agent_comp_remaining_amount == null ? null : Number(data.agent_comp_remaining_amount));
    if (data.agent_comp_earned_at !== undefined) setAgentCompEarnedAt(data.agent_comp_earned_at || null);
    if (data.agent_comp_paid_at !== undefined) setAgentCompPaidAt(data.agent_comp_paid_at || null);
    if (data.paid_at !== undefined) setPaidAt(data.paid_at || null);
    if (data.date) setOrderDate(parseDate(data.date));
    if (data.fatturazione) setFatt((prev) => ({ ...prev, ...(data.fatturazione || {}) }));
    if (data.spedizione) setSped((prev) => ({ ...prev, ...(data.spedizione || {}) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        status,
        payment_method: paymentMethod,
        messaggio_agente: messaggio,
        items: items.map((r) => ({
          product: r.product,
          quantity: Number(r.quantity) || 0,
          unit_price: getUnitPriceLocal(r), // invio sempre il prezzo
        })),
        per_nome: perNome,
        ...fatt,
        ...sped,
      };

      const res = await fetch(`${API_URL}/api/ordini/${id}/`, {
        method: "PATCH",
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore salvataggio");
      refreshFromPayload(data);
      alert("✅ Salvato");
    } catch (e) {
      alert("❌ " + (e?.message || "Errore salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!window.confirm("Confermi l’invio dell’ordine? Non potrai più modificarlo.")) return;
    try {
      const res = await fetch(`${API_URL}/api/ordini/${id}/invia/`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore invio");
      alert("📨 Ordine inviato");
      nav("/ordini/miei");
    } catch (e) {
      alert("❌ " + (e?.message || "Errore invio"));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Eliminare permanentemente la bozza?")) return;
    try {
      const res = await fetch(`${API_URL}/api/ordini/${id}/`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || "Errore eliminazione");
      }
      nav("/ordini/miei");
    } catch (e) {
      alert("❌ " + (e?.message || "Errore eliminazione"));
    }
  };

  // Azioni MADRE
  const postAction = async (endpoint, okMsg = null) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, { method: "POST", headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore operazione");
      refreshFromPayload(data);
      if (okMsg) alert(okMsg);
    } catch (e) {
      alert("❌ " + (e?.message || "Errore operazione"));
    }
  };
  const handleMarkPaid = async () => postAction(`/api/ordini/${id}/paga/`, "✅ Ordine segnato come pagato");
  const handleMarkUnpaid = async () =>
    postAction(`/api/ordini/${id}/non_paga/`, "✅ Pagamento annullato (ordine NON pagato)");

  // PDF
  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ordini/${id}/pdf/`, {
        method: "GET",
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || "Errore generazione PDF");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ordine_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("❌ " + (e?.message || "Errore PDF"));
    }
  };
  // ====== FATTURAZIONE dall'ordine ======
  const createInvoiceFromOrder = async () => {
    if (invoiceId) {
      if (window.confirm("Esiste già una fattura per questo ordine. Vuoi aprirla?")) {
        nav(`/fatture/${invoiceId}`);
      }
      return;
    }
    if (!window.confirm("Creare una fattura a partire da questo ordine?")) return;
    try {
      const r = await fetch(`${API_URL}/api/fatture/da-ordine/`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: Number(id), series: "PF" }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || "Errore creazione fattura da ordine");
      const invId = data?.id;
      if (invId) {
        setInvoiceId(invId);
        setInvoiceStatus(data?.status || "");
        nav(`/fatture/${invId}`);
      } else {
        // in caso l'API risponda senza body ma con Location:
        const loc = r.headers.get("Location");
        if (loc) {
          // prova ad estrarre id dall'URL /api/fatture/:id/
          const m = loc.match(/\/fatture\/(\d+)/);
          if (m) {
            setInvoiceId(Number(m[1]));
            nav(`/fatture/${Number(m[1])}`);
            return;
          }
        }
        // altrimenti aggiorna elenco fatture per ordine
        try {
          const invRes = await fetch(`${API_URL}/api/fatture/?order=${id}`, { headers: authHeaders() });
          const invText = await invRes.text();
          const invList = parseList(invText);
          if (invList.length) {
            setInvoiceId(invList[0].id);
            setInvoiceStatus(invList[0].status || "");
            nav(`/fatture/${invList[0].id}`);
            return;
          }
        } catch {}
        alert("✅ Fattura creata.");
      }
    } catch (e) {
      alert("❌ " + (e?.message || "Errore creazione fattura"));
    }
  };

  if (loading) return <div className="container mt-4">Caricamento…</div>;

  const isAppClienteOrder = senderRole === "CLIENTE";
  const editingTotalsOnClient = status === "DRAFT" && !isMadre;
  const displaySubtotal = editingTotalsOnClient ? Number((items || []).reduce((a, it) => a + (Number(it.quantity || 0) * getUnitPriceLocal(it)), 0)) : Number(subtotal || 0);
  const displayTotal = editingTotalsOnClient ? displaySubtotal : Number(total || 0);

  const now = new Date();
  const daysSinceOrder =
    orderDate ? Math.floor((now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
  const isOver30Unpaid = !isPaid && typeof daysSinceOrder === "number" && daysSinceOrder > 30;
  const warnUnpaidFulfilled = !isPaid && (status === "FULFILLED" || status === "EVASO" || status === "APPROVED");

  const isInternal = ["MADRE", "SEDE", "PV", "FRANCHISING"].includes((meRole || "").toUpperCase());
  const isOwnOrder = meId !== null && Number(senderId) === Number(meId);
  const canCreateProforma = isInternal && meId !== null && !isOwnOrder;
  const compLabel = (() => {
    const st = (agentCompStatus || '').toUpperCase();
    if (st === 'PAID') return `Pagato${agentCompPaidAt ? ' (' + agentCompPaidAt + ')' : ''}`;
    if (st === 'EARNED') return `Maturato${agentCompEarnedAt ? ' (' + agentCompEarnedAt + ')' : ''}`;
    return 'In attesa';
  })();
  const compBadgeClass = (() => {
    const st = (agentCompStatus || '').toUpperCase();
    if (st === 'PAID') return 'badge bg-success';
    if (st === 'EARNED') return 'badge bg-primary';
    return 'badge bg-light text-dark';
  })();

  const updateAppStatus = async (nextStatus) => {
    setUpdatingAppStatus(true);
    try {
      const r = await fetch(`${API_URL}/api/ordini/${id}/app-status/`, {
        method: "POST",
        headers: {
          ...authHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ app_status: nextStatus }),
      });

      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail || "Errore aggiornamento stato app");

      const oRes = await fetch(`${API_URL}/api/ordini/${id}/`, { headers: authHeaders() });
      const oData = await oRes.json().catch(() => ({}));
      if (!oRes.ok) throw new Error(oData?.detail || "Errore refresh ordine");
      refreshFromPayload(oData);
    } catch (e) {
      alert("❌ " + (e?.message || "Errore aggiornamento stato app"));
    } finally {
      setUpdatingAppStatus(false);
    }
  };

  const handlePayB2BCommission = async () => {
    if (!["MADRE", "SEDE"].includes(meRole)) return;
    if (!["AGENTE", "AZIENDA"].includes(senderRole)) return;
    if (!isPaid || agentCompStatus !== "EARNED") return;

    const rawAmount = window.prompt("Importo provvigione da liquidare (€):");
    if (rawAmount === null) return;

    const normalized = rawAmount.trim().replace(",", ".");
    const amount = Number(normalized);

    if (!/^\\d+(?:\\.\\d{1,2})?$/.test(normalized) || !Number.isFinite(amount) || amount <= 0) {
      alert("Inserisci un importo positivo con massimo due decimali.");
      return;
    }

    const methodInput = window.prompt(
      "Metodo di pagamento: BONIFICO, CONTANTI oppure ALTRO",
      "BONIFICO"
    );
    if (methodInput === null) return;

    const payment_method = methodInput.trim().toUpperCase();
    if (!["BONIFICO", "CONTANTI", "ALTRO"].includes(payment_method)) {
      alert("Metodo di pagamento non valido.");
      return;
    }

    const reference = window.prompt("Riferimento pagamento (facoltativo):", "");
    if (reference === null) return;
    const notes = window.prompt("Note (facoltative):", "");
    if (notes === null) return;

    if (reference.length > 160 || notes.length > 5000) {
      alert("Riferimento o note troppo lunghi.");
      return;
    }

    if (!window.confirm(
      `Confermi la registrazione di € ${amount.toFixed(2)} tramite ${payment_method} per l'ordine #${id}?`
    )) return;

    setPayingAgentComp(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ordini/${id}/registra-pagamento-agente/`,
        {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: amount.toFixed(2),
            payment_method,
            reference: reference.trim(),
            notes: notes.trim(),
          }),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.detail || "Pagamento non registrato");

      const refreshed = await fetch(`${API_URL}/api/ordini/${id}/`, {
        headers: authHeaders(),
      });
      if (!refreshed.ok) throw new Error("Pagamento registrato, ma aggiornamento ordine non riuscito");
      refreshFromPayload(await refreshed.json());

      alert(
        `Pagamento registrato: € ${amount.toFixed(2)}. Residuo: € ${Number(result.residual || 0).toFixed(2)}`
      );
    } catch (error) {
      alert(error.message || "Errore registrazione pagamento. Verificare prima di riprovare.");
    } finally {
      setPayingAgentComp(false);
    }
  };

  const handlePayAgentComp = async () => {
    setPayingAgentComp(true);
    try {
      const r = await fetch(`${API_URL}/api/ordini/${id}/paga-agente/`, { method: 'POST', headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail || 'Errore pagamento compenso');
      const oRes = await fetch(`${API_URL}/api/ordini/${id}/`, { headers: authHeaders() });
      const oData = await oRes.json().catch(() => ({}));
      if (!oRes.ok) throw new Error(oData?.detail || 'Errore refresh ordine');
      refreshFromPayload(oData);
      alert('✅ Compenso pagato');
    } catch (e) {
      alert('❌ ' + (e?.message || 'Errore pagamento compenso'));
    } finally {
      setPayingAgentComp(false);
    }
  };

  return (
    <div className="container mt-4">
      <button
        className="btn btn-outline-secondary mb-3"
          onClick={() => window.history.length > 1 ? nav(-1) : nav(isMadre ? "/ordini" : "/ordini/miei")}
      >
        ← Torna
      </button>

      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center gap-2">
          <h2 className="m-0">Ordine #{id}</h2>
          <span className="badge bg-secondary">{status}</span>
          {isAppClienteOrder && (
            <span className="badge bg-success">App: {appStatusLabel || appStatus}</span>
          )}
          {isPaid ? (
            <span className="badge bg-success">
              PAGATO {paidAt ? `(${paidAt})` : ""}
            </span>
          ) : (
            <span className="badge bg-warning text-dark">NON PAGATO</span>
          )}
            {!isAppClienteOrder && invoiceId && (
              <span className="badge bg-info text-dark" title={`Proforma #${invoiceId} ${invoiceStatus || ""}`}>
                Proforma collegata
              </span>
            )}
            {isInternal && (
              <span className={compBadgeClass} title={agentCompPct ? `Compenso agente: ${agentCompPct}% — € ${Number(agentCompAmount || 0).toFixed(2)}` : undefined}>
                Compenso: {compLabel}
              </span>
            )}
        </div>

        <div className="d-flex gap-2 flex-wrap">
            {isAppClienteOrder && (
              <div className="d-flex flex-wrap gap-1 align-items-center">
                {APP_STATUS_FLOW.map((st) => {
                  const active = (appStatus || "INVIATO").toUpperCase() === st.value;
                  return (
                    <button
                      key={st.value}
                      type="button"
                      className={`btn btn-sm ${active ? "btn-success" : "btn-outline-secondary"}`}
                      disabled={updatingAppStatus || active}
                      onClick={() => updateAppStatus(st.value)}
                    >
                      {updatingAppStatus ? "..." : st.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Proforma */}
            {!isAppClienteOrder && canCreateProforma && !invoiceId && (
              <button className="btn btn-outline-primary" onClick={createInvoiceFromOrder}>
                Crea proforma
              </button>
            )}

            {!isAppClienteOrder && invoiceId && (
              <button className="btn btn-primary" onClick={() => nav(`/fatture/${invoiceId}`)}>
                Apri proforma
              </button>
            )}

          {!isMadre && (
            <>
              <button className="btn btn-outline-danger" onClick={handleDelete} disabled={status !== "DRAFT"}>
                Elimina
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={!editingEnabled || saving}>
                {saving ? "Salvataggio…" : "Salva"}
              </button>
              <button className="btn btn-success" onClick={handleSubmit} disabled={!editingEnabled}>
                Invia
              </button>
            </>
          )}

          {isMadre && !isAppClienteOrder && (
            <>
              {status === "SUBMITTED" && (
                <>
                  <button className="btn btn-success" onClick={() => postAction(`/api/ordini/${id}/approva/`, "✅ Ordine approvato")}>
                    Approva
                  </button>
                  <button className="btn btn-danger" onClick={() => postAction(`/api/ordini/${id}/rifiuta/`, "✅ Ordine rifiutato")}>
                    Rifiuta
                  </button>
                </>
              )}
              {status === "APPROVED" && (
                <>
                  <button className="btn btn-primary" onClick={() => postAction(`/api/ordini/${id}/evadi/`, "✅ Ordine evaso")}>
                    Evadi
                  </button>
                  <button className="btn btn-danger" onClick={() => postAction(`/api/ordini/${id}/rifiuta/`, "✅ Ordine rifiutato")}>
                    Rifiuta
                  </button>
                </>
              )}
              {!isPaid ? (
                <button className="btn btn-warning" onClick={handleMarkPaid}>
                  Segna pagato
                </button>
              ) : (
                <button className="btn btn-outline-warning" onClick={handleMarkUnpaid}>
                  Segna NON pagato
                </button>
              )}
            </>
          )}

          {["MADRE", "SEDE"].includes(meRole) &&
            ["AGENTE", "AZIENDA"].includes(senderRole) &&
            isPaid &&
            agentCompStatus === "EARNED" && (
              <button
                className="btn btn-success"
                onClick={handlePayB2BCommission}
                disabled={payingAgentComp}
              >
                {payingAgentComp ? "Registrazione…" : "Registra pagamento provvigione B2B"}
              </button>
            )}

          {isInternal && !["AGENTE", "AZIENDA"].includes(senderRole) && isPaid && (agentCompStatus || "").toUpperCase() === "EARNED" && (
            <button className="btn btn-success" onClick={handlePayAgentComp} disabled={payingAgentComp}>
              {payingAgentComp ? "Pagamento…" : "Paga agente"}
            </button>
          )}

          <button className="btn btn-outline-secondary" onClick={downloadPdf}>
            PDF
          </button>
        </div>
      </div>

      {(isOver30Unpaid || warnUnpaidFulfilled) && (
        <div className={`alert ${isOver30Unpaid ? "alert-danger" : "alert-warning"} mb-3`}>
          {!isPaid && isOver30Unpaid && (
            <div><strong>Pagamento scaduto.</strong> Ordine non pagato da {daysSinceOrder} giorni.</div>
          )}
          {!isPaid && warnUnpaidFulfilled && (
            <div>Ordine evaso/approvato ma non ancora pagato.</div>
          )}
        </div>
      )}

      <div className="row g-3">
        {/* Sinistra: righe */}
        <div className="col-12 col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Righe</strong>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={addRow}
                disabled={!editingEnabled || products.length === 0}
              >
                + Aggiungi riga
              </button>
            </div>

            {editingEnabled ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 280 }}>Prodotto</th>
                      <th style={{ width: 110 }}>Qtà</th>
                      <th style={{ width: 140 }}>Prezzo unit.</th>
                      <th style={{ width: 140 }}>Subtotale</th>
                      <th style={{ width: 60 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-muted py-4">Nessuna riga.</td>
                      </tr>
                    )}
                    {items.map((r, i) => {
                      const up = getUnitPriceLocal(r);
                      const line = (Number(r.quantity || 0) * up);
                      return (
                        <tr key={i}>
                          <td>
                            <select
                              className="form-select"
                              value={r.product}
                              onChange={(e) => setItemField(i, "product", Number(e.target.value))}
                              disabled={products.length === 0}
                            >
                              {products.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control"
                              min="0"
                              step="1"
                              value={r.quantity || 0}
                              onChange={(e) => setItemField(i, "quantity", e.target.value)}
                            />
                          </td>
                          <td>€ {Number(up || 0).toFixed(2)}</td>
                          <td>€ {Number(line || 0).toFixed(2)}</td>
                          <td className="text-end">
                            <button className="btn btn-outline-danger btn-sm" onClick={() => removeRow(i)}>
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="list-group list-group-flush">
                {items.length === 0 && (
                  <div className="list-group-item text-muted">Nessuna riga.</div>
                )}
                {items.map((r, i) => (
                  <div key={i} className="list-group-item d-flex justify-content-between">
                    <div>{r._name || products.find((p) => Number(p.id) === Number(r.product))?.name || `#${r.product}`}</div>
                    <div>
                      x {Number(r.quantity).toFixed(0)} — € {Number(r.unit_price || 0).toFixed(2)} →{" "}
                      <strong>€ {Number(r.line_subtotal || 0).toFixed(2)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Destra: riepilogo + impostazioni */}
        <div className="col-12 col-lg-4">
          <div className="card shadow-sm mb-3">
            <div className="card-header">
              <strong>Riepilogo</strong>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <span>Subtotale</span>
                <strong>€ {displaySubtotal.toFixed(2)}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Totale</span>
                <strong>€ {displayTotal.toFixed(2)}</strong>
              </div>

              {isInternal &&
                ["AGENTE", "AZIENDA"].includes(senderRole) &&
                Number(agentCompAmount || 0) > 0 && (
                  <div className="border-top mt-3 pt-2">
                    <div className="d-flex justify-content-between">
                      <span>Provvigione agente B2B ({Number(agentCompPct || 0).toFixed(2)}%)</span>
                      <strong>€ {Number(agentCompAmount).toFixed(2)}</strong>
                    </div>
                    <small className="text-muted">
                      Stato: {agentCompStatus || "Non maturata"}
                    </small>
                    <div className="d-flex justify-content-between mt-2">
                      <span>Già liquidato</span>
                      <strong>
                        {agentCompPaidAmount == null
                          ? "Non disponibile"
                          : `€ ${agentCompPaidAmount.toFixed(2)}`}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Residuo da liquidare</span>
                      <strong>
                        {agentCompRemainingAmount == null
                          ? "Non disponibile"
                          : `€ ${agentCompRemainingAmount.toFixed(2)}`}
                      </strong>
                    </div>
                  </div>
                )}

              {isAgente && Number(commissionAmount || 0) > 0 && (
                <div className="d-flex justify-content-between mt-2">
                  <span className="text-muted">
                    Provvigione ({Number(commissionPercent || 0).toFixed(2)}%)
                  </span>
                  <span className="text-muted">€ {Number(commissionAmount).toFixed(2)}</span>
                </div>
              )}

              {editingEnabled && (
                <small className="text-muted d-block mt-2">
                  (Anteprima client: il totale ufficiale è calcolato dal server al salvataggio.)
                </small>
              )}
            </div>
          </div>

          <div className="card shadow-sm mb-3">
            <div className="card-header">
              <strong>Impostazioni ordine</strong>
            </div>
            <div className="card-body">
              <label className="form-label">Metodo pagamento</label>
              <select
                className="form-select mb-3"
                value={paymentMethod}
                disabled={!editingEnabled}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="RITIRO">Contanti / Carta</option>
                <option value="CONSEGNA">Pagamento alla consegna</option>
                <option value="BONIFICO">Bonifico Bancario</option>
                <option value="ONLINE">Pagamento Online</option>
              </select>

              <label className="form-label">Messaggio agente</label>
              <textarea
                className="form-control"
                rows={3}
                disabled={!editingEnabled}
                value={messaggio}
                onChange={(e) => setMessaggio(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dati ordine */}
      <div className="card shadow-sm mt-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>Dati dell’ordine</strong>
          {/* ✅ Toggle sincronia Spedizione = Fatturazione */}
          <div className="d-flex align-items-center gap-3">
            <div className="form-check m-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="syncShipBilling"
                disabled={!editingEnabled}
                checked={syncShipBilling}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setSyncShipBilling(checked);
                  if (checked) {
                    // copia immediata
                    setSped((prev) => ({
                      ...prev,
                      ...billingToShippingSnapshot(fatt, perNome),
                    }));
                  }
                }}
              />
              <label className="form-check-label" htmlFor="syncShipBilling">
                Spedizione uguale alla fatturazione
              </label>
            </div>
            {!syncShipBilling && (
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={!editingEnabled}
                onClick={() =>
                  setSped((prev) => ({
                    ...prev,
                    ...billingToShippingSnapshot(fatt, perNome),
                  }))
                }
                title="Copia una tantum i dati di fatturazione nella spedizione"
              >
                Copia ora
              </button>
            )}
          </div>
        </div>

        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Per chi (etichetta breve)</label>
            {isAgente ? (
              <select
                className="form-select"
                disabled={!editingEnabled}
                value={selectedAziendaId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedAziendaId(v);

                  const az = aziendeAgente.find((x) => String(x.id) === String(v));
                  if (!az) return;

                  setPerNome(az.name || "");
                  setFatt((prev) => ({
                    ...prev,
                    fatt_ragione_sociale: az.name || "",
                    fatt_piva: az.vat || "",
                    fatt_email: az.email || "",
                  }));
                  setSped((prev) => ({
                    ...prev,
                    sped_destinatario: az.name || "",
                    sped_telefono: az.phone || "",
                  }));

                  (async () => {
                    try {
                      const dRes = await fetch(`${API_URL}/api/aziende/${az.code}/`, {
                        headers: authHeaders(),
                      });
                      const d = await dRes.json().catch(() => ({}));
                      if (!dRes.ok) return;

                        setFatt((prev) => ({
                          ...prev,
                          fatt_ragione_sociale: d.name || az.name || "",
                          fatt_piva: d.vat || az.vat || "",
                          fatt_cf: d.cf || prev.fatt_cf || "",
                          fatt_email: d.email || az.email || "",
                          fatt_pec: d.pec || prev.fatt_pec || "",
                          fatt_indirizzo: d.address || "",
                          fatt_cap: d.cap || prev.fatt_cap || "",
                          fatt_citta: d.citta || prev.fatt_citta || "",
                          fatt_provincia: d.provincia || prev.fatt_provincia || "",
                          fatt_nazione: d.nazione || prev.fatt_nazione || "",
                          fatt_sdi: d.unique_code || prev.fatt_sdi || "",
                        }));

                        setSped((prev) => ({
                          ...prev,
                          sped_destinatario: d.name || az.name || "",
                          sped_indirizzo: d.address || "",
                          sped_cap: d.cap || prev.sped_cap || "",
                          sped_citta: d.citta || prev.sped_citta || "",
                          sped_provincia: d.provincia || prev.sped_provincia || "",
                          sped_nazione: d.nazione || prev.sped_nazione || "",
                          sped_telefono: d.phone || az.phone || prev.sped_telefono || "",
                          sped_note: d.notes || "",
                        }));
                    } catch {}
                  })();
                }}
              >
                <option value="">Seleziona azienda…</option>
                {aziendeAgente.map((az) => (
                  <option key={az.id} value={az.id}>
                    {az.name} ({az.code})
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="form-control"
                placeholder="Es. Bar Rossi"
                disabled={!editingEnabled}
                value={perNome}
                onChange={(e) => setPerNome(e.target.value)}
              />
            )}
            <small className="text-muted">Appare come riferimento rapido del destinatario dell’ordine.</small>
          </div>

          <h6 className="text-uppercase text-muted mt-4">Fatturazione</h6>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Ragione sociale</label>
              <input className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_ragione_sociale}
                onChange={(e) => setFatt({ ...fatt, fatt_ragione_sociale: e.target.value })}/>
            </div>
            <div className="col-md-3">
              <label className="form-label">Partita IVA</label>
              <input className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_piva}
                onChange={(e) => setFatt({ ...fatt, fatt_piva: e.target.value })}/>
            </div>
            <div className="col-md-3">
              <label className="form-label">Codice Fiscale</label>
              <input className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_cf}
                onChange={(e) => setFatt({ ...fatt, fatt_cf: e.target.value })}/>
            </div>

            <div className="col-md-6">
              <label className="form-label">Indirizzo</label>
              <input className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_indirizzo}
                onChange={(e) => setFatt({ ...fatt, fatt_indirizzo: e.target.value })}/>
            </div>
            <div className="col-md-2">
              <label className="form-label">CAP</label>
              <input className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_cap}
                onChange={(e) => setFatt({ ...fatt, fatt_cap: e.target.value })}/>
            </div>
            <div className="col-md-4">
              <label className="form-label">Città</label>
              <input className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_citta}
                onChange={(e) => setFatt({ ...fatt, fatt_citta: e.target.value })}/>
            </div>
            <div className="col-md-3">
              <label className="form-label">Provincia</label>
              <input className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_provincia}
                onChange={(e) => setFatt({ ...fatt, fatt_provincia: e.target.value })}/>
            </div>
            <div className="col-md-3">
              <label className="form-label">Nazione</label>
              <input className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_nazione}
                onChange={(e) => setFatt({ ...fatt, fatt_nazione: e.target.value })}/>
            </div>
            <div className="col-md-3">
              <label className="form-label">Email fatturazione</label>
              <input type="email" className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_email}
                onChange={(e) => setFatt({ ...fatt, fatt_email: e.target.value })}/>
            </div>
            <div className="col-md-3">
              <label className="form-label">PEC</label>
              <input className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_pec}
                onChange={(e) => setFatt({ ...fatt, fatt_pec: e.target.value })}/>
            </div>
            <div className="col-md-3">
              <label className="form-label">Codice SDI</label>
              <input className="form-control" disabled={!editingEnabled}
                value={fatt.fatt_sdi}
                onChange={(e) => setFatt({ ...fatt, fatt_sdi: e.target.value })}/>
            </div>
          </div>

          <h6 className="text-uppercase text-muted mt-4">Spedizione</h6>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Destinatario</label>
              <input className="form-control" disabled={!editingEnabled || syncShipBilling}
                value={sped.sped_destinatario}
                onChange={(e) => setSped({ ...sped, sped_destinatario: e.target.value })}/>
            </div>
            <div className="col-md-6">
              <label className="form-label">Indirizzo</label>
              <input className="form-control" disabled={!editingEnabled || syncShipBilling}
                value={sped.sped_indirizzo}
                onChange={(e) => setSped({ ...sped, sped_indirizzo: e.target.value })}/>
            </div>
            <div className="col-md-2">
              <label className="form-label">CAP</label>
              <input className="form-control" disabled={!editingEnabled || syncShipBilling}
                value={sped.sped_cap}
                onChange={(e) => setSped({ ...sped, sped_cap: e.target.value })}/>
            </div>
            <div className="col-md-4">
              <label className="form-label">Città</label>
              <input className="form-control" disabled={!editingEnabled || syncShipBilling}
                value={sped.sped_citta}
                onChange={(e) => setSped({ ...sped, sped_citta: e.target.value })}/>
            </div>
            <div className="col-md-3">
              <label className="form-label">Provincia</label>
              <input className="form-control" disabled={!editingEnabled || syncShipBilling}
                value={sped.sped_provincia}
                onChange={(e) => setSped({ ...sped, sped_provincia: e.target.value })}/>
            </div>
            <div className="col-md-3">
              <label className="form-label">Nazione</label>
              <input className="form-control" disabled={!editingEnabled || syncShipBilling}
                value={sped.sped_nazione}
                onChange={(e) => setSped({ ...sped, sped_nazione: e.target.value })}/>
            </div>
            <div className="col-md-4">
              <label className="form-label">Telefono</label>
              <input className="form-control" disabled={!editingEnabled}
                value={sped.sped_telefono}
                onChange={(e) => setSped({ ...sped, sped_telefono: e.target.value })}/>
            </div>
            <div className="col-md-8">
              <label className="form-label">Note</label>
              <input className="form-control" disabled={!editingEnabled}
                value={sped.sped_note}
                onChange={(e) => setSped({ ...sped, sped_note: e.target.value })}/>
            </div>
          </div>

          {!isMadre && (
            <div className="text-end mt-3">
              <button className="btn btn-primary" onClick={handleSave} disabled={!editingEnabled || saving}>
                {saving ? "Salvataggio…" : "Salva modifiche"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
